import { BoatCardImage } from '@components/BoatCardImage';
import styles from '@styles/CompareBoats/search_card.module.scss';
import { getBoatName } from '@utils/getBoatName';
import { Button } from 'antd';
import React, { useMemo } from 'react';
import { StoredBoat, useAddToCompare } from './hooks/useCompare';

/**
 * A boat as the Meilisearch `boat` index holds it.
 *
 * Card-sized, and deliberately so: the index backs search-as-you-type boxes that each draw a
 * picture, a name, a maker and a year. It is not a spec sheet, and the compare table no longer
 * tries to build one out of it — everything below the picker is read from Sanity by `_id`.
 *
 * `manufacturer` is the maker's *name*, not an object: the index flattens the reference.
 *
 * Narrower than the index document, because `CompareSearchPanel` pins this panel's
 * `attributesToRetrieve`: a field missing from that list never reaches this component, however
 * faithfully the index carries it.
 */
type BoatHit = {
  [key: string]: unknown;
  _id?: string;
  id?: string | number;
  image_id?: string;
  manufacturer?: string;
  model?: string;
  yearFirst?: number;
};

type Props = {
  hit: BoatHit;
};

/**
 * The index carries the cover image twice — as an asset id and as a plain URL. Only the asset id is
 * kept: it is what `SanityImage` renders, and the URL's host is not one `next/image` will serve.
 */
const toStoredImage = (assetId: string | undefined): StoredBoat['attributes']['image'] =>
  assetId ? { _type: 'image', asset: { _ref: assetId, _type: 'reference' } } : undefined;

const SearchCard: React.FC<Props> = ({ hit }) => {
  const boatId = String(hit._id ?? hit.id ?? '');

  /**
   * Only what the picker card needs. The specs are fetched from Sanity against this id when the
   * compare table renders, so copying anything else out of the hit would just be a second, staler
   * source for the same fields.
   */
  const boatToBeSaved: StoredBoat = useMemo(
    () => ({
      _id: boatId,
      attributes: {
        image: toStoredImage(hit.image_id),
        manufacturer: hit.manufacturer ? { name: hit.manufacturer } : undefined,
        model: hit.model,
        yearFirst: hit.yearFirst,
      },
    }),
    [boatId, hit.image_id, hit.manufacturer, hit.model, hit.yearFirst],
  );

  const addToCompare = useAddToCompare();

  const boatTitle = useMemo(
    () =>
      getBoatName({
        yearFirst: hit.yearFirst,
        model: hit.model,
        manufacturer: { name: hit.manufacturer },
      }),
    [hit.yearFirst, hit.model, hit.manufacturer],
  );

  const cardImage = useMemo(
    () => (boatToBeSaved.attributes.image ? { ...boatToBeSaved.attributes.image, alt: boatTitle } : null),
    [boatToBeSaved.attributes.image, boatTitle],
  );

  return (
    <div className={styles.container}>
      <div className={styles.containerMask}>
        <Button
          onClick={() => {
            addToCompare(boatToBeSaved);
          }}
          className={styles.maskButton}
          disabled={!boatId}
        >
          Add to compare
        </Button>
      </div>

      {/*
        `BoatCardImage` rather than `SanityImage` directly, because a boat with no cover image
        gets a placeholder from it instead of an exception: `sanity-image` treats the empty
        string this used to fall back to as a missing `id` and throws while rendering.

        The alt is applied here rather than stored, so the stored boat stays a record of what she
        is and not of how one card happened to label her picture.
      */}
      <div className={styles.containerImage}>
        <BoatCardImage image={cardImage} width={160} height={120} />
      </div>

      <div className={styles.containerContent}>
        <h3 className={styles.contentTitle}>{boatTitle}</h3>

        {/*
          The view count that used to sit here has been removed. It read `viewsCount`, which no
          revision of the search index has ever carried — not the current card-sized document and
          not the 69-field one before it — so the eye icon has only ever been drawn beside an empty
          space. Putting it back means indexing `viewsCount` and naming it in
          `BOAT_COMPARE_ATTRIBUTES`, not restoring this markup.
        */}
      </div>
    </div>
  );
};

export default SearchCard;
